/**
 * Hook for managing user list filters and search state
 *
 * Extracted from useUserManagement.js to reduce complexity.
 * Handles search text, role filters, status filters, and organization filters.
 */
import { useState, useCallback, useEffect } from 'react';

import { getOrganizationAccess, getIsAccountOwner } from '../util/storage';
import {
  getDefaultRoles,
  getAdminRoles,
  getStatusOptions,
  getEscalationTierOptions,
  filterUsers as filterUsersUtil,
} from '../util/user';

/**
 * Custom hook for user filtering state and logic
 *
 * @param {string} organization - The current organization
 * @param {Object} existingUsers - All loaded users
 * @param {string} mode - Current mode (create/edit)
 * @returns {Object} Filter state and handlers
 */
export default function useUserFilters(organization, existingUsers = {}, mode = 'create') {
  // Search text
  const [searchText, setSearchText] = useState('');

  // Role filter state
  const [roleSearchOpen, setRoleSearchOpen] = useState(false);
  const [roleSearchValue, setRoleSearchValue] = useState([]);
  const [roleSearchFilter, setRoleSearchFilter] = useState(getDefaultRoles());

  // Account owner status (affects available role filters)
  const [isAccountOwner, setIsAccountOwner] = useState(false);

  // Check if current user is an account owner (can see admin roles in filter)
  useEffect(() => {
    getIsAccountOwner().then((isOwner) => {
      setIsAccountOwner(isOwner);
      if (isOwner) {
        setRoleSearchFilter([...getDefaultRoles(), ...getAdminRoles()]);
      }
    });
  }, []);

  // Status filter state
  const [statusSearchOpen, setStatusSearchOpen] = useState(false);
  const [statusSearchValue, setStatusSearchValue] = useState([]);
  const [statusSearchFilter, setStatusSearchFilter] = useState(getStatusOptions());

  // Escalation tier filter state
  const [tierSearchOpen, setTierSearchOpen] = useState(false);
  const [tierSearchValue, setTierSearchValue] = useState([]);
  const [tierSearchFilter, setTierSearchFilter] = useState(getEscalationTierOptions());

  // Organization filter state (for filtering user list)
  const [validOrgs, setValidOrgs] = useState([]);
  const [loadingOrg, setLoadingOrg] = useState(true);
  const [OrgFilterOpen, setOrgFilterOpen] = useState(false);
  const [OrgFilterValue, setOrgFilterValue] = useState([organization]);
  const [OrgFilter, setOrgFilter] = useState([organization]);

  // Organization filters for create mode (primary org and org access)
  const [OrgFilterOpen2, setOrgFilterOpen2] = useState(false);
  const [OrgFilterValue2, setOrgFilterValue2] = useState(organization);
  const [OrgFilter2, setOrgFilter2] = useState([]);
  const [OrgFilterOpen3, setOrgFilterOpen3] = useState(false);
  const [OrgFilterValue3, setOrgFilterValue3] = useState([organization]);
  const [OrgFilter3, setOrgFilter3] = useState([]);

  // Filtered results
  const [filteredUsers, setFilteredUsers] = useState({});
  const [showUsers, setShowUsers] = useState(false);

  /**
   * Load valid organizations from storage
   */
  const updateValidOrgs = useCallback(async () => {
    let organization_access = await getOrganizationAccess();
    organization_access = organization_access.length ? organization_access : [organization];
    setValidOrgs(organization_access);
    const orgFilters = organization_access.map((x) => ({ label: x, value: x }));
    setOrgFilter(orgFilters);
    setOrgFilter2(orgFilters);
    setOrgFilter3(orgFilters);
    setLoadingOrg(false);
  }, [organization]);

  /**
   * Reset filters to default state
   */
  const resetFilters = useCallback(() => {
    setSearchText('');
    setRoleSearchValue([]);
    setStatusSearchValue([]);
    setTierSearchValue([]);
    setOrgFilterValue([organization]);
  }, [organization]);

  // Note: updateValidOrgs() is called by the parent hook (useUserManagement) to coordinate
  // with user data loading. This hook does not auto-initialize orgs.

  // Update showUsers based on mode and search
  useEffect(() => {
    setShowUsers(mode !== 'create' || searchText.length > 1);
  }, [mode, searchText]);

  // Filter users when criteria change
  useEffect(() => {
    // Don't filter if existingUsers hasn't loaded yet
    if (Object.keys(existingUsers).length === 0) {
      return;
    }

    // Skip if in create mode with no/short search
    if (mode === 'create' && searchText.length <= 1) {
      return;
    }

    // Check if any filter is active
    const hasActiveFilters =
      searchText?.length > 1 ||
      roleSearchValue.length > 0 ||
      statusSearchValue.length > 0 ||
      tierSearchValue.length > 0;

    if (hasActiveFilters || mode !== 'create') {
      // Perform filtering
      const matches = filterUsersUtil(existingUsers, {
        searchText,
        organizations: OrgFilterValue || [],
        roles: roleSearchValue,
        statuses: statusSearchValue,
        escalationTiers: tierSearchValue,
      });
      setFilteredUsers(matches);
    }
  }, [searchText, existingUsers, roleSearchValue, statusSearchValue, tierSearchValue, OrgFilterValue, mode]);


  return {
    // Search
    searchText,
    setSearchText,

    // Role filters
    roleSearchOpen,
    setRoleSearchOpen,
    roleSearchValue,
    setRoleSearchValue,
    roleSearchFilter,
    setRoleSearchFilter,

    // Status filters
    statusSearchOpen,
    setStatusSearchOpen,
    statusSearchValue,
    setStatusSearchValue,
    statusSearchFilter,
    setStatusSearchFilter,

    // Escalation tier filters
    tierSearchOpen,
    setTierSearchOpen,
    tierSearchValue,
    setTierSearchValue,
    tierSearchFilter,
    setTierSearchFilter,

    // Organization filters (list filtering)
    validOrgs,
    loadingOrg,
    OrgFilterOpen,
    setOrgFilterOpen,
    OrgFilterValue,
    setOrgFilterValue,
    OrgFilter,
    setOrgFilter,

    // Organization filters (create mode)
    OrgFilterOpen2,
    setOrgFilterOpen2,
    OrgFilterValue2,
    setOrgFilterValue2,
    OrgFilter2,
    setOrgFilter2,
    OrgFilterOpen3,
    setOrgFilterOpen3,
    OrgFilterValue3,
    setOrgFilterValue3,
    OrgFilter3,
    setOrgFilter3,

    // Filtered results
    filteredUsers,
    setFilteredUsers,
    showUsers,

    // Actions
    resetFilters,
    updateValidOrgs,
  };
}
